import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ChevronDown,
  Loader2,
  Phone,
  PhoneIncoming,
  PhoneMissed,
  PhoneOutgoing,
  Play,
  Sparkles,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import voiceService, { CALLS_PAGE_SIZE, type CallRecord } from '@/services/voice.service';

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function formatWhen(iso: string, locale: string): string {
  const d = new Date(iso);
  return d.toLocaleString(locale, {
    day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

/**
 * Anrufliste mit Wiedergabe der Aufzeichnungen.
 *
 * Der Wiedergabe-Link wird ERST BEIM KLICK vom Server geholt: die von Telnyx
 * gelieferten S3-Links verfallen nach 10 Minuten, gespeichert ist nur der
 * Marker „Aufzeichnung vorhanden".
 */
export default function Calls() {
  const { t, i18n } = useTranslation();
  const [pages, setPages] = useState<CallRecord[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  /** callId → frische Audio-URL; null = wird gerade geladen. */
  const [players, setPlayers] = useState<Record<string, string | null>>({});
  /** callId → gerade in Arbeit befindliche Zusammenfassung. */
  const [summarizing, setSummarizing] = useState<Record<string, boolean>>({});
  /** callId → Transkript aufgeklappt. */
  const [openTranscripts, setOpenTranscripts] = useState<Record<string, boolean>>({});

  const { isLoading } = useQuery({
    queryKey: ['voice-calls'],
    queryFn: async () => {
      const page = await voiceService.listCalls({ limit: CALLS_PAGE_SIZE, offset: 0 });
      setPages(page.items);
      setOffset(page.items.length);
      setHasMore(page.hasMore);
      setTotal(page.total);
      return page;
    },
    refetchInterval: 30_000,
  });

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const next = await voiceService.listCalls({ limit: CALLS_PAGE_SIZE, offset });
      setPages((prev) => {
        const seen = new Set(prev.map((p) => p.id));
        return [...prev, ...next.items.filter((i) => !seen.has(i.id))];
      });
      setOffset((prev) => prev + next.items.length);
      setHasMore(next.hasMore);
      setTotal(next.total);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, offset]);

  const openRecording = async (call: CallRecord) => {
    if (players[call.id] !== undefined) {
      // Player zuklappen — beim nächsten Klick wird ein frischer Link geholt.
      setPlayers((p) => { const n = { ...p }; delete n[call.id]; return n; });
      return;
    }
    setPlayers((p) => ({ ...p, [call.id]: null }));
    try {
      const url = await voiceService.getRecordingUrl(call.id);
      setPlayers((p) => ({ ...p, [call.id]: url }));
    } catch {
      setPlayers((p) => { const n = { ...p }; delete n[call.id]; return n; });
      toast.error(t('shell:calls.recordingFailed', 'Aufzeichnung konnte nicht geladen werden'));
    }
  };

  const runSummary = async (call: CallRecord) => {
    setSummarizing((s) => ({ ...s, [call.id]: true }));
    try {
      const r = await voiceService.summarizeCall(call.id);
      // Ergebnis direkt in die Liste schreiben — kein Refetch nötig.
      setPages((prev) => prev.map((c) => (c.id === call.id ? { ...c, summary: r.summary, transcript: r.transcript } : c)));
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('shell:calls.summaryFailed', 'Zusammenfassung fehlgeschlagen'));
    } finally {
      setSummarizing((s) => ({ ...s, [call.id]: false }));
    }
  };

  const takeover = async (call: CallRecord) => {
    try {
      await voiceService.takeoverCall(call.id);
      toast.success(t('shell:calls.takeoverOk', 'Wird übernommen — der Anruf klingelt gleich bei Ihnen'));
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('shell:calls.takeoverFailed', 'Übernahme fehlgeschlagen'));
    }
  };

  const statusBadge = (call: CallRecord) => {
    if (call.status === 'completed') {
      return (
        <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">
          {formatDuration(call.duration_seconds)}
        </Badge>
      );
    }
    if (call.status === 'queued') {
      return (
        <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">
          {t('shell:calls.queued', 'Warteschlange')}
        </Badge>
      );
    }
    if (call.status === 'active' || call.status === 'ringing') {
      return (
        <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-700">
          {t('shell:calls.live', 'Läuft')}
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="border-red-200 bg-red-50 text-red-600">
        {call.hangup_cause === 'no_agent_online'
          ? t('shell:calls.noAgent', 'Niemand online')
          : t('shell:calls.missed', 'Verpasst')}
      </Badge>
    );
  };

  return (
    <div className="mx-auto max-w-4xl px-6 py-8">
      <div className="mb-6 flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">
            {t('shell:calls.title', 'Anrufe')}
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            {t('shell:calls.subtitle', 'Ein- und ausgehende Telefonate mit Aufzeichnungen')}
          </p>
        </div>
        {total > 0 && <span className="text-sm tabular-nums text-slate-500">{total}</span>}
      </div>

      {isLoading && pages.length === 0 ? (
        <div className="flex justify-center py-16 text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : pages.length === 0 ? (
        <p className="rounded-xl border border-dashed border-slate-200 py-14 text-center text-sm text-slate-500 dark:border-white/10">
          {t('shell:calls.empty', 'Noch keine Anrufe')}
        </p>
      ) : (
        <ul className="divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200 bg-white dark:divide-white/5 dark:border-white/10 dark:bg-white/[0.02]">
          {pages.map((call) => {
            const missed = call.direction === 'inbound' && !call.answered_at;
            const Icon = missed ? PhoneMissed : call.direction === 'inbound' ? PhoneIncoming : PhoneOutgoing;
            const counterpart = call.direction === 'inbound' ? call.from_number : call.to_number;
            const agent = [call.first_name, call.last_name].filter(Boolean).join(' ');
            const player = players[call.id];

            return (
              <li key={call.id} className="px-4 py-3">
                <div className="flex items-center gap-3">
                  <span
                    className={cn(
                      'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
                      missed
                        ? 'bg-red-50 text-red-500 dark:bg-red-500/10'
                        : call.direction === 'inbound'
                          ? 'bg-blue-50 text-blue-600 dark:bg-blue-500/10'
                          : 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10',
                    )}
                  >
                    <Icon className="h-4 w-4" />
                  </span>

                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium tabular-nums text-slate-900 dark:text-slate-100">
                      {counterpart || t('shell:voice.unknownNumber', 'Unbekannt')}
                    </p>
                    <p className="truncate text-xs text-slate-500">
                      {formatWhen(call.started_at, i18n.language)}
                      {agent && ` · ${agent}`}
                      {call.ticket_subject && ` · ${call.ticket_subject}`}
                    </p>
                  </div>

                  {call.handled_by === 'ai' && (
                    <Badge variant="outline" className="border-violet-200 bg-violet-50 text-violet-700">
                      <Sparkles className="mr-1 h-3 w-3" />
                      {t('shell:calls.aiBadge', 'KI')}
                    </Badge>
                  )}

                  {statusBadge(call)}

                  {/* Canlı KI görüşmesini insan devralabilir */}
                  {call.handled_by === 'ai' && call.status === 'active' && (
                    <Button
                      size="sm"
                      className="h-8 shrink-0 rounded-md bg-violet-600 text-white hover:bg-violet-700"
                      onClick={() => void takeover(call)}
                    >
                      {t('shell:calls.takeover', 'Übernehmen')}
                    </Button>
                  )}

                  {call.recording_url && (
                    <>
                      {/* Özet: kayıt varsa ve henüz özet yoksa üretilebilir */}
                      {!call.summary && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 shrink-0 rounded-md"
                          onClick={() => void runSummary(call)}
                          disabled={Boolean(summarizing[call.id])}
                          title={t('shell:calls.summarize', 'KI-Zusammenfassung erstellen')}
                        >
                          {summarizing[call.id] ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Sparkles className="h-3.5 w-3.5" />
                          )}
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 shrink-0 rounded-md"
                        onClick={() => void openRecording(call)}
                      >
                        {player === null ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Play className="mr-1.5 h-3.5 w-3.5" />
                        )}
                        {player !== null && t('shell:calls.recording', 'Aufzeichnung')}
                      </Button>
                    </>
                  )}
                </div>

                {call.summary && (
                  <div className="mt-2 pl-12">
                    <div className="rounded-lg border border-violet-200 bg-violet-50/60 px-3 py-2 text-xs leading-5 text-slate-700 dark:border-violet-500/20 dark:bg-violet-500/[0.06] dark:text-slate-300">
                      <p className="mb-1 flex items-center gap-1 font-semibold text-violet-700 dark:text-violet-400">
                        <Sparkles className="h-3 w-3" />
                        {t('shell:calls.summary', 'KI-Zusammenfassung')}
                      </p>
                      <p className="whitespace-pre-wrap">{call.summary}</p>
                      {call.transcript && (
                        <button
                          type="button"
                          className="mt-1.5 flex items-center gap-1 text-[11px] font-medium text-violet-600 hover:underline dark:text-violet-400"
                          onClick={() => setOpenTranscripts((s) => ({ ...s, [call.id]: !s[call.id] }))}
                        >
                          <ChevronDown className={cn('h-3 w-3 transition-transform', openTranscripts[call.id] && 'rotate-180')} />
                          {t('shell:calls.transcript', 'Transkript')}
                        </button>
                      )}
                      {openTranscripts[call.id] && (
                        <p className="mt-1 whitespace-pre-wrap border-t border-violet-200/60 pt-1.5 text-[11px] text-slate-500 dark:border-violet-500/20">
                          {call.transcript}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {typeof player === 'string' && (
                  <div className="mt-2 pl-12">
                    {/* Frischer 10-Minuten-Link — S3 braucht keine Auth-Header,
                        deshalb funktioniert das native <audio> hier direkt. */}
                    <audio controls autoPlay src={player} className="h-9 w-full max-w-md" />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {hasMore && (
        <div className="mt-4 flex justify-center">
          <Button variant="outline" size="sm" onClick={() => void loadMore()} disabled={loadingMore}>
            {loadingMore && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
            {t('shell:calls.loadMore', 'Mehr laden')}
          </Button>
        </div>
      )}
    </div>
  );
}
